import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  MenuItem,
  TextField,
  Typography,
  Stack,
  Slider,
} from "@mui/material";
import { unwrapBoxes } from "../../utils/adventureLayoutFormat";
import { imageUrl, boxRect } from "../../utils/scenarioVariants";
import MotionScenarioPreview from "./MotionScenarioPreview";
import MineCartPlacementPreview from "./MineCartPlacementPreview";
function assetLeaves(v, p = "", out = {}) {
  if (v?.asset || v?.url) out[p] = v;
  else if (v && typeof v === "object")
    Object.entries(v).forEach(([k, val]) =>
      assetLeaves(val, p ? `${p}.${k}` : k, out),
    );
  return out;
}
export default function ScenarioLayoutPreview({ config, scenarioId }) {
  const wagonnet = scenarioId === "wagonnet_fou";
  const avalanche = scenarioId === "avalanche",
    course = scenarioId === "course_poursuite";
  const moving = wagonnet || avalanche || course;
  const [journey, setJourney] = useState(0.3);
  const [progress, setProgress] = useState(0.75);
  const [showBoxes, setShowBoxes] = useState(true),
    [slot, setSlot] = useState(""),
    [boxName, setBoxName] = useState("");
  const boxes = unwrapBoxes(config.layout),
    images = useMemo(
      () =>
        (wagonnet || avalanche) && config.obstacleIllustrations
          ? config.obstacleIllustrations.map((row, i) => [
              `obstacleIllustrations.${i}.image.source`,
              row.image?.source,
            ])
          : Object.entries(assetLeaves(config)).filter(([p]) =>
              wagonnet
                ? p.startsWith("images.obstacles.")
                : avalanche
                  ? p.startsWith("images.projectiles.")
                  : p !== "background",
            ),
      [config, wagonnet, avalanche],
    );
  const slotsKey = images.map(([path]) => path).join("|");
  const boxesKey = boxes.map((b) => b.name).join("|");
  useEffect(() => {
    setSlot(images[0]?.[0] || "");
    // La boîte où le jeu pose l'image inspectée, sinon la première : la course au trésor
    // n'en avait aucune ici, et son coffre s'affichait au milieu de la piste du joueur.
    setBoxName(
      boxes.find((b) =>
        /guard_zone|sage_zone|artisan_zone|mannequin_zone|lock_zone|race_finish|scene/.test(
          b.name,
        ),
      )?.name ||
        boxes[0]?.name ||
        "",
    );
  }, [slotsKey, boxesKey]);
  const image = images.find(([p]) => p === slot)?.[1],
    box = boxes.find((b) => b.name === boxName),
    rect = box && boxRect(box, boxes);
  return (
    <Stack spacing={1}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "1408 / 752",
          overflow: "hidden",
          bgcolor: "#eee",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {config.background && (
          <Box
            component="img"
            src={imageUrl(config.background)}
            alt="Fond de la variante"
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "fill",
            }}
          />
        )}
        {wagonnet && (
          <MineCartPlacementPreview
            config={config}
            slot={slot}
            progress={progress}
          />
        )}
        {(avalanche || course) && (
          <MotionScenarioPreview
            config={config}
            scenarioId={scenarioId}
            progress={progress}
            journey={journey}
            slot={slot}
          />
        )}
        {!moving && image && rect && (
          <Box
            component="img"
            src={imageUrl(image)}
            alt={slot}
            sx={{
              position: "absolute",
              left: `${rect.x}%`,
              top: `${rect.y}%`,
              width: `${rect.w}%`,
              height: `${rect.h}%`,
              objectFit: "contain",
            }}
          />
        )}
        {showBoxes &&
          boxes.map((b) => {
            const r = boxRect(b, boxes);
            return (
              r && (
                <Box
                  key={b.id}
                  onClick={() => setBoxName(b.name)}
                  component="button"
                  aria-label={`Sélectionner la boîte ${b.name}`}
                  sx={{
                    position: "absolute",
                    left: `${r.x}%`,
                    top: `${r.y}%`,
                    width: `${r.w}%`,
                    height: `${r.h}%`,
                    border: "1px solid",
                    borderColor: b.name === boxName ? "#f59e0b" : "#2196f3",
                    backgroundColor: "rgba(33,150,243,.04)",
                    textAlign: "left",
                    verticalAlign: "top",
                    cursor: "pointer",
                    p: 0,
                    color: "#064581",
                    fontSize: 10,
                    overflow: "hidden",
                  }}
                >
                  {b.name}
                </Box>
              )
            );
          })}
      </Box>
      <FormControlLabel
        control={
          <Switch checked={showBoxes} onChange={(_, v) => setShowBoxes(v)} />
        }
        label="Afficher les boîtes du layout"
      />
      {images.length > 0 && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            fullWidth
            select
            size="small"
            label={
              wagonnet || avalanche
                ? "Obstacle à inspecter"
                : "Image / état à inspecter"
            }
            value={images.some(([p]) => p === slot) ? slot : ""}
            onChange={(e) => setSlot(e.target.value)}
          >
            {images.map(([p]) => (
              <MenuItem value={p} key={p}>
                {p.startsWith("obstacleIllustrations.")
                  ? config.obstacleIllustrations[Number(p.split(".")[1])].label
                  : p}
              </MenuItem>
            ))}
          </TextField>
          {!moving && (
            <TextField
              fullWidth
              select
              size="small"
              label="Afficher dans la boîte"
              value={boxes.some((b) => b.name === boxName) ? boxName : ""}
              onChange={(e) => setBoxName(e.target.value)}
            >
              {boxes.map((b) => (
                <MenuItem value={b.name} key={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      )}
      {moving && (
        <Box>
          <Typography variant="caption">
            {course
              ? "Avancement de la course"
              : avalanche
                ? "Chute du projectile"
                : "Approche de l’obstacle"}
          </Typography>
          <Slider
            aria-label={
              course
                ? "Avancement de la course"
                : avalanche
                  ? "Chute du projectile"
                  : "Approche de l’obstacle"
            }
            value={progress}
            min={0}
            max={1}
            step={0.01}
            onChange={(_, v) => setProgress(v)}
          />
        </Box>
      )}
      {avalanche && (
        <Box>
          <Typography variant="caption">
            Progression du compagnon sur la pente
          </Typography>
          <Slider
            aria-label="Progression du compagnon"
            min={0}
            max={1}
            step={0.01}
            value={journey}
            onChange={(_, v) => setJourney(v)}
          />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        {moving
          ? "Placement calculé comme dans le jeu. Les personnages sont des repères schématiques ; les décors procéduraux et les animations ne sont pas simulés."
          : "Aperçu de placement statique. L’image sélectionnée est centrée dans la boîte choisie ; les animations et le compagnon du joueur ne sont pas simulés."}
      </Typography>
    </Stack>
  );
}
